// Story Problems: word problems starring her, the neighbors and her pet. Number pad answers,
// with a "show me the steps" hint that reveals one step at a time.

import { h, clear } from '@kit/dom';
import type { GameModule, GameContext } from '@shell/types';
import { makeNumpad } from '@games/common/numpad';
import { makeStory, type StoryProblem } from '@problems/story-problems';

const PROBLEMS = 6;

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let p!: StoryProblem;
  let level = ctx.currentLevel();
  let maxLevel = level;
  let attempts = 0, correct = 0;
  let misses = 0, hinted = false, locked = false, stepsShown = 0;
  const used = new Set<number>();
  const names = { kid: ctx.avatar.persona.name || 'Autumn', pet: 'your pet' };
  const t0 = performance.now();
  let tProblem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Problem ', h('strong', null, '1'), ` of ${PROBLEMS}`), h('button.btn.small.ghost', { onclick: () => ctx.speak(p.text) }, 'Read it'));
  const card = h('div.card', { style: { fontSize: '19px', lineHeight: '1.5', fontWeight: '700', minHeight: '96px' } });
  const stepsBox = h('div.stack', { style: { gap: '4px', minHeight: '24px' } });
  const unitLine = h('p.center.muted.small', { style: { minHeight: '18px' } });
  const padHost = h('div');
  const hintBtn = h('button.btn.small.ghost.block', { onclick: showStep }, 'Show me the steps');
  host.append(head, card, stepsBox, unitLine, padHost, hintBtn);

  let pad = makeNumpad({ onEnter: answer, enterLabel: 'Go', allowDecimal: true });
  padHost.append(pad.el);

  function newProblem() {
    level = ctx.currentLevel();
    maxLevel = Math.max(maxLevel, level);
    p = makeStory(level, ctx.rng, names, used);
    misses = 0; hinted = false; locked = false; stepsShown = 0;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    card.textContent = p.text;
    clear(stepsBox);
    unitLine.textContent = p.unit === '$' ? 'Answer in dollars.' : p.unit ? `Answer in ${p.unit}.` : '';
    pad.clear(); pad.setEnabled(true);
    hintBtn.style.display = '';
    hintBtn.textContent = 'Show me the steps';
  }

  function showStep() {
    if (stepsShown >= p.steps.length - 1) return;
    hinted = true;
    stepsBox.append(h('p.small', { style: { fontWeight: '700', color: 'var(--text-2)' } }, `Step ${stepsShown + 1}: ${p.steps[stepsShown]}`));
    stepsShown++;
    if (stepsShown >= p.steps.length - 1) hintBtn.style.display = 'none';
    else hintBtn.textContent = 'Show the next step';
    ctx.sound('pop');
  }

  function answer(v: number) {
    if (locked) return;
    const ms = performance.now() - tProblem;
    if (Math.abs(v - p.answer) < 1e-9) {
      locked = true; pad.setEnabled(false);
      attempts++; correct++;
      ctx.report({ skill: p.skill, level, correct: true, hintUsed: hinted, ms, problemId: p.id });
      ctx.sound('ding');
      ctx.say(`Yes! ${p.steps[p.steps.length - 1]}`);
      next();
    } else {
      misses++;
      if (misses === 1) { attempts++; ctx.report({ skill: p.skill, level, correct: false, hintUsed: hinted, ms, problemId: p.id }); }
      ctx.sound('buzz');
      card.classList.remove('shake'); void card.offsetWidth; card.classList.add('shake');
      pad.clear();
      if (misses >= 2) {
        locked = true; pad.setEnabled(false);
        clear(stepsBox);
        p.steps.forEach((s, i) => stepsBox.append(h('p.small', { style: { fontWeight: '700', color: 'var(--text-2)' } }, `Step ${i + 1}: ${s}`)));
        ctx.say(`The answer is ${fmt(p.answer)}${p.unit === '$' ? ' dollars' : p.unit ? ' ' + p.unit : ''}.`);
        next(2600);
      } else {
        hinted = true;
        if (stepsShown === 0) showStep();
        ctx.say(`Not ${fmt(v)}. Read the step and try once more.`);
      }
    }
  }

  function fmt(v: number): string { return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100); }

  function next(delay = 1100) {
    n++;
    if (n >= PROBLEMS) {
      if (attempts === correct && maxLevel >= 5) ctx.charm('charm.story');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), delay);
      return;
    }
    setTimeout(newProblem, delay);
  }

  newProblem();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'math.story-problems',
  title: 'Story Problems',
  blurb: 'Word problems about you, your pet and the neighbors. Read, think, type the answer.',
  category: 'math',
  skills: ['add_sub', 'mult_div', 'fractions'],
  levelRange: [1, 10],
  unlock: { stars: 11 },
  mount,
};
export default mod;
